import { ArticleContentType } from '@/components/Articles/new-articles/utils/types/articles-type'
import {
  TStrapiV4PageType,
  TStrapiV4OneListingWidget,
} from '@/stories/strapi-4/common-types'

export const generateSlug = (text: string): string => {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')
}

const addIdsToHeadings = (
  content: string,
  allTableOfContents: Array<{ text: string; slug: string }>,
) => {
  // Replace each h2 tag while collecting headings
  const modifiedContent = content.replace(
    /<h2>(?:<strong>)?(.*?)(?:<\/strong>)?<\/h2>/g,
    (match: string, headingText: string) => {
      const text = headingText
        .replace(/&nbsp;/g, ' ') // Replace &nbsp; with a space
        .replace(/&amp;/g, '&') // Handle &amp; if present
        .replace(/<[^>]+>/g, '') // Remove any other HTML tags
        .trim()
      const slug = generateSlug(text)

      allTableOfContents.push({ text, slug })

      // If the heading has a strong tag
      if (match.includes('<strong>')) {
        return `<h2 id="${slug}"><strong>${text}</strong></h2>`
      }
      // If the heading doesn't have a strong tag
      return `<h2 id="${slug}">${text}</h2>`
    },
  )

  return {
    modifiedContent,
  }
}

const extractInsightsTitles = (
  insightsData?: { data?: { sections?: TStrapiV4OneListingWidget[] } } | null,
): Array<{ text: string; slug: string }> => {
  const insightsTOC: Array<{ text: string; slug: string }> = []

  if (insightsData?.data?.sections) {
    for (const section of insightsData.data.sections) {
      if (section.title) {
        insightsTOC.push({
          text: section.title,
          slug: generateSlug(section.title),
        })
      }
    }
  }

  return insightsTOC
}

export const getTableOfContent = (
  strapiApiData?: TStrapiV4PageType | null,
  insightsData?: { data?: { sections?: TStrapiV4OneListingWidget[] } } | null,
): Array<{ text: string; slug: string }> => {
  const allTableOfContents: Array<{ text: string; slug: string }> = []
  const insightsTOC = extractInsightsTitles(insightsData)
  let insightsAdded = false

  // Check if MFInsights placeholder exists
  const hasMFInsightsPlaceholder = strapiApiData?.data.attributes.content.some(
    el =>
      el.__component === 'widgets.dynamic-place-holder-v0' &&
      el.widget_type === 'MFInsights',
  )

  if (strapiApiData) {
    for (const ele of strapiApiData.data.attributes.content) {
      if (ele.__component === 'widgets.rich-text-editor') {
        const content = ele.rich_text_html
        const { modifiedContent } = addIdsToHeadings(
          content,
          allTableOfContents,
        )

        ele.rich_text_html = modifiedContent
      } else if (
        ele.__component === 'widgets.dynamic-place-holder-v0' &&
        ele.widget_type === 'MFInsights'
      ) {
        // Add insights at MFInsights placeholder position
        if (!insightsAdded && insightsTOC.length > 0) {
          allTableOfContents.push(...insightsTOC)
          insightsAdded = true
        }
      } else if (ele.__component === 'widgets.faqs-section') {
        // Add insights before FAQ section only if no MFInsights placeholder
        if (
          !hasMFInsightsPlaceholder &&
          !insightsAdded &&
          insightsTOC.length > 0
        ) {
          allTableOfContents.push(...insightsTOC)
          insightsAdded = true
        }

        if (ele.faq_section.heading) {
          allTableOfContents.push({
            text: ele.faq_section.heading,
            slug: generateSlug(ele.faq_section.heading),
          })
        }
      }
    }
  }

  // If insights weren't added yet (no MFInsights placeholder and no FAQ), add at the end
  if (!insightsAdded && insightsTOC.length > 0) {
    allTableOfContents.push(...insightsTOC)
  }

  return allTableOfContents
}

export const getBlogTableOfContent = (
  contentArr?: ArticleContentType[] | null,
): Array<{ text: string; slug: string }> => {
  const allTableOfContents: Array<{ text: string; slug: string }> = []

  if (contentArr) {
    for (const ele of contentArr) {
      if (ele.__component === 'common.rich-text-editor') {
        const content = ele.richTextEditor
        const { modifiedContent } = addIdsToHeadings(
          content ?? '',
          allTableOfContents,
        )

        ele.richTextEditor = modifiedContent
      }
    }
  }
  return allTableOfContents
}
